import React, { useEffect, useRef, useState, useCallback, Children } from "react";
import { ActionIcon, Tabs, TabsProps } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { ChevronLeft, ChevronRight } from "tabler-icons-react";

export interface ScrollableTabsProps extends TabsProps {
  children: React.ReactNode;
}

const BaseTabItem = Tabs.Tab as unknown as React.ComponentType<{
  children?: React.ReactNode;
  color?: string;
  label?: React.ReactNode;
  tabKey?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}>;

export function ScrollableTabs({
  children,
  active,
  onTabChange,
  variant,
  styles,
  style,
  ...rest
}: ScrollableTabsProps) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollByTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const tabsArray = Children.toArray(children);
  const tabsCount = tabsArray.length;
  const activeIndex = typeof active === "number" ? active : 0;

  const getWrapperElement = useCallback((): HTMLElement | null => {
    if (!containerRef.current) return null;
    return containerRef.current.querySelector(".mantine-Tabs-tabsListWrapper") as HTMLElement | null;
  }, []);

  const updateScrollIndicators = useCallback(() => {
    const wrapper = getWrapperElement();
    if (!wrapper) return;

    const { scrollLeft, scrollWidth, clientWidth } = wrapper;

    // In non-layout environments (e.g. JSDOM where dimensions are 0) or before layout paint on mobile
    if (scrollWidth === 0 && clientWidth === 0) {
      if (isMobile && tabsCount > 2) {
        setCanScrollLeft(activeIndex > 0);
        setCanScrollRight(activeIndex < tabsCount - 1);
      } else {
        setCanScrollLeft(false);
        setCanScrollRight(false);
      }
      return;
    }

    const overflow = scrollWidth - clientWidth;
    if (overflow <= 4) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    setCanScrollLeft(scrollLeft > 4);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 4);
  }, [getWrapperElement, isMobile, tabsCount, activeIndex]);

  useEffect(() => {
    updateScrollIndicators();
    const timer = setTimeout(updateScrollIndicators, 60);

    const wrapper = getWrapperElement();
    const tabsList = containerRef.current?.querySelector(".mantine-Tabs-tabsList") as HTMLElement | null;

    if (wrapper) {
      wrapper.addEventListener("scroll", updateScrollIndicators, { passive: true });
    }
    window.addEventListener("resize", updateScrollIndicators);

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined" && wrapper) {
      resizeObserver = new ResizeObserver(() => {
        updateScrollIndicators();
      });
      resizeObserver.observe(wrapper);
      if (tabsList) {
        resizeObserver.observe(tabsList);
      }
    }

    return () => {
      clearTimeout(timer);
      if (scrollByTimerRef.current) {
        clearTimeout(scrollByTimerRef.current);
      }
      if (wrapper) {
        wrapper.removeEventListener("scroll", updateScrollIndicators);
      }
      window.removeEventListener("resize", updateScrollIndicators);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [updateScrollIndicators, getWrapperElement]);

  // Keep the active tab scrolled into view when changed
  useEffect(() => {
    const wrapper = getWrapperElement();
    if (!wrapper) return;
    const tabButtons = wrapper.querySelectorAll('[role="tab"]');
    const activeButton = tabButtons[activeIndex] as HTMLElement | undefined;
    if (activeButton && typeof activeButton.scrollIntoView === "function") {
      try {
        activeButton.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
      } catch {
        // Fallback for older browsers/test environments
      }
    }
    const timer = setTimeout(updateScrollIndicators, 150);
    return () => clearTimeout(timer);
  }, [activeIndex, getWrapperElement, updateScrollIndicators]);

  const handleScrollBy = (direction: "left" | "right") => {
    const wrapper = getWrapperElement();
    if (!wrapper) return;

    const delta = Math.max(160, Math.round((wrapper.clientWidth || 240) * 0.65));
    const offset = direction === "right" ? delta : -delta;

    if (typeof wrapper.scrollBy === "function") {
      try {
        wrapper.scrollBy({ left: offset, behavior: "smooth" });
      } catch {
        wrapper.scrollLeft += offset;
      }
    } else {
      wrapper.scrollLeft += offset;
    }

    if (scrollByTimerRef.current) {
      clearTimeout(scrollByTimerRef.current);
    }
    scrollByTimerRef.current = setTimeout(updateScrollIndicators, 200);
  };

  const resolvedVariant = variant ?? (isMobile ? "pills" : "default");

  return (
    <div
      ref={containerRef}
      className="captiva-scrollable-tabs"
      style={{ position: "relative", width: "100%", ...style }}
    >
      {canScrollLeft && (
        <div
          data-testid="tabs-scroll-left-indicator"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            height: isMobile ? 44 : 40,
            display: "flex",
            alignItems: "center",
            paddingLeft: 2,
            paddingRight: 18,
            background:
              "linear-gradient(to right, #ffffff 55%, rgba(255, 255, 255, 0.9) 75%, rgba(255, 255, 255, 0))",
            zIndex: 5,
            pointerEvents: "none",
          }}
        >
          <ActionIcon
            data-testid="tabs-scroll-left"
            aria-label="Scroll tabs left"
            variant="default"
            radius="xl"
            size={isMobile ? 32 : 28}
            onClick={() => handleScrollBy("left")}
            style={{
              pointerEvents: "auto",
              boxShadow: "0 1px 4px rgba(0, 0, 0, 0.14)",
              border: "1px solid #ced4da",
              backgroundColor: "#ffffff",
              color: "#1B2678",
              minWidth: isMobile ? 32 : 28,
              minHeight: isMobile ? 32 : 28,
            }}
          >
            <ChevronLeft size={18} />
          </ActionIcon>
        </div>
      )}

      {canScrollRight && (
        <div
          data-testid="tabs-scroll-right-indicator"
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            height: isMobile ? 44 : 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            paddingLeft: 22,
            paddingRight: 2,
            background:
              "linear-gradient(to left, #ffffff 55%, rgba(255, 255, 255, 0.9) 75%, rgba(255, 255, 255, 0))",
            zIndex: 5,
            pointerEvents: "none",
          }}
        >
          <ActionIcon
            data-testid="tabs-scroll-right"
            aria-label="More tabs"
            variant="default"
            radius="xl"
            size={isMobile ? 32 : 28}
            onClick={() => handleScrollBy("right")}
            style={{
              pointerEvents: "auto",
              boxShadow: "0 1px 4px rgba(0, 0, 0, 0.14)",
              border: "1px solid #ced4da",
              backgroundColor: "#ffffff",
              color: "#1B2678",
              minWidth: isMobile ? 32 : 28,
              minHeight: isMobile ? 32 : 28,
            }}
          >
            <ChevronRight size={18} />
          </ActionIcon>
        </div>
      )}

      <Tabs
        active={active}
        onTabChange={onTabChange}
        variant={resolvedVariant}
        styles={(theme) => {
          const userStyles = typeof styles === "function" ? styles(theme) : styles || {};
          return {
            ...userStyles,
            tabsListWrapper: {
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
              ...(isMobile
                ? {
                    paddingBottom: 6,
                    borderBottom: `1px solid ${theme.colors.gray[2]}`,
                    marginBottom: theme.spacing.md,
                  }
                : {}),
              ...(userStyles.tabsListWrapper || {}),
            },
            tabsList: {
              flexWrap: "nowrap",
              display: "flex",
              ...(isMobile
                ? {
                    gap: 6,
                    width: "max-content",
                    minWidth: "100%",
                    paddingLeft: canScrollLeft ? 36 : 0,
                    paddingRight: canScrollRight ? 40 : 0,
                  }
                : {
                    paddingLeft: canScrollLeft ? 32 : 0,
                    paddingRight: canScrollRight ? 36 : 0,
                  }),
              ...(userStyles.tabsList || {}),
            },
            tabControl: {
              whiteSpace: "nowrap",
              flexShrink: 0,
              ...(isMobile
                ? {
                    minHeight: 44,
                    height: 44,
                    padding: "0 14px",
                    borderRadius: 22,
                    fontSize: 14,
                    fontWeight: 500,
                  }
                : {}),
              ...(userStyles.tabControl || {}),
            },
          };
        }}
        {...rest}
      >
        {children}
      </Tabs>
    </div>
  );
}

ScrollableTabs.Tab = BaseTabItem;

export default ScrollableTabs;
